import * as uuid from "uuid";
import socketIOClient from "socket.io-client";
import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { action } from "typesafe-actions";

import api from "../../../api";
import { APIResponse } from "../../../types/APIResponseType";
import {
  AdGroup,
  AdGroupCreationBody,
  AdGroupTemplate,
  AdGroupTemplateCreationBody,
  AdGroupType,
  CreativeAsset,
  CreativeAssetsByAdGroup,
} from "../../../types/adGroupTypes";
import {
  AdGroupToCreateType,
  CAMPAIGN_STEPS,
  CampaignBasicDetailsType,
  CampaignPageTypes,
  CampaignTemplate,
  CampaignType,
  CookieSet,
  CustomDomainList,
  FoundCompaniesInfo,
  FoundEmailsInfo,
  FoundHubspotPage,
  FoundHubspotPages,
  PublicDomainList,
  PublicDomainListData,
  RoleTitleGroup,
} from "../../../types/campaignTypes";
import { AdvertiserType } from "../../../types/advertiserTypes";
import { CampaignCreatorStore } from "../../../types/ReduxTypes/StoreTypes";
import { CreativeAssetFromFile, CreativeAssetToCreate } from "../../../types/creativeTypes";
import { EmailRegExp } from "../../../constants/regExp";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import {
  SET_ADGROUP_TEMPLATES,
  SET_ADGROUP_TO_CREATE,
  SET_CAMPAIGN_LANDING_PAGE,
  SET_CAMPAIGN_ROLE_TITLE_COOKIES,
  SET_CAMPAIGN_STEPS,
  SET_CAMPAIGN_TEMPLATE,
  SET_CAMPAIGN_TEMPLATE_NAME,
  SET_CAMPAIGN_TO_CREATE_BASIC_DETAILS,
  SET_CAMPAIGN_TO_CREATE_SELECTED_ADVERTISER,
  SET_COMPANY_SORT_VALUES,
  SET_CREATED_CUSTOM_DOMAIN_LIST,
  SET_CUSTOM_DOMAIN_LISTS,
  SET_FOUND_COMPANY_INFO,
  SET_FOUND_EMAILS_INFO,
  SET_FOUND_HUBSPOT_PAGES,
  SET_GEO_TARGET_OPTIONS,
  SET_HUBSPOT_LISTS,
  SET_IS_PENDING,
  SET_PUBLIC_DOMAIN_LISTS,
  SET_RESET_CAMPAIGN_TO_CREATE,
  SET_ROLE_TITLE_LIST,
  SET_SEGMENTS_INCLUDE,
  SET_SELECTED_ADGROUP_TEMPLATE,
  SET_SELECTED_DOMAIN_LISTS,
  SET_SELECTED_DOMAIN_LISTS_IDS,
  SET_SELECTED_GEO_TARGET_OPTIONS,
  SET_SELECTED_SEGMENT_IDS,
  SET_SELECTED_ZIP_CODES,
  SET_UPDATED_CUSTOM_DOMAIN_LISTS,
  SET_USE_ADGROUP_TEMPLATE,
  SetAdGroupToCreate,
  SetCampaignBasicDetails,
  SetCampaignCookies,
  SetCampaignLandingPage,
  SetCampaignSteps,
  SetCampaignTemplate,
  SetCampaignTemplateName,
  SetCompanySortValues,
  SetCreatedCustomDomainList,
  SetCustomDomainLists,
  SetEmailsToHash,
  SetFoundCompanyInfo,
  SetFoundHubspotPages,
  SetGeoTargetOptions,
  SetHubSpotLists,
  SetIsPending,
  SetPublicDomainLists,
  SetResetCampaignToCreate,
  SetRoleTitleList,
  SetSegmentsInclude,
  SetSelectedAdGroupTemplate,
  SetSelectedDomainLists,
  SetSelectedDomainListsIds,
  SetSelectedGeoTargetOptions,
  SetSelectedSegmentIds,
  SetSelectedZipCodes,
  SetUpdatedCustomDomainLists,
  SetUseAdGroupTemplate,
} from "./types";
import { errorToaster } from "../../../helpers/toasters";
import { getCookieStrings } from "../../../utils/getCookieStrings";
import { handleApiResponse } from "../../../utils/handleApiResponse";
import { processEmails } from "../../../utils/processEmails";
import { setApiError } from "../apiError/actions";
import { validateHubSpotData } from "../../../helpers/validateHubSpotData";

export const setIsPending = (isPending: boolean): SetIsPending => {
  return action(SET_IS_PENDING, isPending);
};

export const setResetCampaignToCreate = (): SetResetCampaignToCreate => {
  return action(SET_RESET_CAMPAIGN_TO_CREATE);
};

export const setCampaignBasicDetails = (
  campaignBasicDetails: Partial<CampaignBasicDetailsType>,
): SetCampaignBasicDetails => {
  return action(SET_CAMPAIGN_TO_CREATE_BASIC_DETAILS, campaignBasicDetails);
};

export const setCampaignToCreateSelectedAdvertiser = (payload: AdvertiserType) =>
  action(SET_CAMPAIGN_TO_CREATE_SELECTED_ADVERTISER, payload);

export const setAdGroupTemplates = (payload: AdGroupTemplate[]) => action(SET_ADGROUP_TEMPLATES, payload);

export const setCompanySortValues = (sortValues: any): SetCompanySortValues => {
  return action(SET_COMPANY_SORT_VALUES, sortValues);
};

export const setHubSpotLists = (lists: any): SetHubSpotLists => {
  return action(SET_HUBSPOT_LISTS, lists);
};

export const setFoundCompanyInfo = (companyInfo: FoundCompaniesInfo | null): SetFoundCompanyInfo => {
  return action(SET_FOUND_COMPANY_INFO, companyInfo);
};

export const setFoundEmailsInfo = (emailsToHash: FoundEmailsInfo | null): SetEmailsToHash => {
  return action(SET_FOUND_EMAILS_INFO, emailsToHash);
};

export const setFoundHubspostPages = (hubspotPages: FoundHubspotPage[]): SetFoundHubspotPages => {
  return action(SET_FOUND_HUBSPOT_PAGES, hubspotPages);
};

export const setRoleTitlesList = (roleTitleList: RoleTitleGroup[]): SetRoleTitleList => {
  return action(SET_ROLE_TITLE_LIST, roleTitleList);
};

export const setGeoTargetOptions = (geoTargetOptions: Array<any>): SetGeoTargetOptions => {
  return action(SET_GEO_TARGET_OPTIONS, geoTargetOptions);
};

export const setCampaignCookies = (cookieSets: Record<string, CookieSet[]> | null): SetCampaignCookies => {
  return action(SET_CAMPAIGN_ROLE_TITLE_COOKIES, cookieSets);
};

export const setCampaignLandingPage = (page: string): SetCampaignLandingPage => {
  return action(SET_CAMPAIGN_LANDING_PAGE, page);
};

export const setAdGroupToCreate = (adGroupToCreate: AdGroupToCreateType): SetAdGroupToCreate => {
  return action(SET_ADGROUP_TO_CREATE, adGroupToCreate);
};

export const setAdGroupTemplate = (useAdGroupTemplate: boolean): SetUseAdGroupTemplate => {
  return action(SET_USE_ADGROUP_TEMPLATE, useAdGroupTemplate);
};

export const setSelectedAdGroupTemplate = (adGroupId: string | null): SetSelectedAdGroupTemplate => {
  return action(SET_SELECTED_ADGROUP_TEMPLATE, adGroupId);
};

export const setSelectedGeoTargetOptions = (selectedGeoTargetOptions: Array<string>): SetSelectedGeoTargetOptions => {
  return action(SET_SELECTED_GEO_TARGET_OPTIONS, selectedGeoTargetOptions);
};

export const setSelectedZipCodes = (selectedZipCodes: Array<string>): SetSelectedZipCodes => {
  return action(SET_SELECTED_ZIP_CODES, selectedZipCodes);
};

export const setSelectedSegmentIds = (selectedSegmentIds: Array<string>): SetSelectedSegmentIds => {
  return action(SET_SELECTED_SEGMENT_IDS, selectedSegmentIds);
};

export const setPublicDomainLists = (publicDomainLists: PublicDomainList[]): SetPublicDomainLists => {
  return action(SET_PUBLIC_DOMAIN_LISTS, publicDomainLists);
};

export const setCustomDomainLists = (customDomainLists: CustomDomainList[]): SetCustomDomainLists => {
  return action(SET_CUSTOM_DOMAIN_LISTS, customDomainLists);
};

export const setCreatedCustomDomainList = (createdCustomDomainList: CustomDomainList): SetCreatedCustomDomainList => {
  return action(SET_CREATED_CUSTOM_DOMAIN_LIST, createdCustomDomainList);
};

export const setUpdatedCustomDomainLists = (
  updatedCustomDomainLists: CustomDomainList[],
): SetUpdatedCustomDomainLists => {
  return action(SET_UPDATED_CUSTOM_DOMAIN_LISTS, updatedCustomDomainLists);
};

export const setSelectedDomainListsIds = (selectedDomainListsIds: Array<number>): SetSelectedDomainListsIds => {
  return action(SET_SELECTED_DOMAIN_LISTS_IDS, selectedDomainListsIds);
};

export const setSelectedDomainLists = (selectedDomainLists: Array<PublicDomainListData>): SetSelectedDomainLists => {
  return action(SET_SELECTED_DOMAIN_LISTS, selectedDomainLists);
};

export const setSegmentsInclude = (domain: string, include: boolean): SetSegmentsInclude => {
  return action(SET_SEGMENTS_INCLUDE, { domain, include });
};

export const setCampaignSteps = (campaignSteps: CAMPAIGN_STEPS[] | null): SetCampaignSteps => {
  return action(SET_CAMPAIGN_STEPS, campaignSteps);
};

export const setCampaignTemplateName = (campaignTemplateName: string | null): SetCampaignTemplateName => {
  return action(SET_CAMPAIGN_TEMPLATE_NAME, campaignTemplateName);
};

export const setCampaignTemplate = (campaignTemplate: CampaignTemplate | null): SetCampaignTemplate => {
  return action(SET_CAMPAIGN_TEMPLATE, campaignTemplate);
};

export const getRoleTitleList = (): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const roleTitleList = await api.getRoleTitleList();
      dispatch(setIsPending(false));
      dispatch(handleApiResponse(roleTitleList, setRoleTitlesList, setApiError));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getRoleTitleList: ", error);
      dispatch(setIsPending(false));
    }
  };
};

export const getGeoTargetOptions = (): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const geoTargetOptions = await api.getGeoTargetOptions();
      dispatch(setIsPending(false));
      dispatch(handleApiResponse(geoTargetOptions, setGeoTargetOptions, setApiError));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getGeoTargetOptions: ", error);
      dispatch(setIsPending(false));
    }
  };
};

export const getAdGroupTemplates = (
  bgAdvertiserId: string,
): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const adGroupTemplates = await api.getAdGroupTemplatesByAdvertiser(bgAdvertiserId);
      dispatch(setIsPending(false));
      dispatch(handleApiResponse(adGroupTemplates, setAdGroupTemplates, setApiError));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getAdGroupTemplates: ", error);
      dispatch(setIsPending(false));
    }
  };
};

export const getCampaignCompanySortValues =
  (): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const { campaignToCreate } = getState();
      const sortValues = await api.getSortValues(campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId);
      dispatch(setIsPending(false));
      dispatch(handleApiResponse(sortValues, setCompanySortValues, setApiError));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getCampaignCompanySortValues: ", error);
      dispatch(setIsPending(false));
    }
  };

export const getHubSpotLists =
  (listType: "contact_lists" | "company_lists"): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const { campaignToCreate } = getState();
      const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);

      const data = {
        bgAdvertiserId: campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId,
        listType,
      };
      socket.emit("getHubSpotLists", data);

      socket.on("retrievedLists", (lists: any) => {
        try {
          dispatch(setIsPending(false));
          socket.close();
          dispatch(handleApiResponse(lists, setHubSpotLists, setApiError));
        } catch (error) {
          dispatch(setApiError((error as any).toString()));
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getHubSpotLists: ", error);
      dispatch(setIsPending(false));
    }
  };

export const getHubspotPages =
  (pageTypes: CampaignPageTypes[]): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      dispatch(setIsPending(true));
      const { campaignToCreate } = getState();
      const bgAdvertiserId = campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId;
      const { payload } = await api.getHubspotPages(bgAdvertiserId, pageTypes);

      const fetchedHubspotPages: FoundHubspotPages = payload;

      const blogPages: FoundHubspotPage[] = [];
      const landingPages: FoundHubspotPage[] = [];
      const webPages: FoundHubspotPage[] = [];

      if (fetchedHubspotPages.blogPosts.length !== 0) {
        fetchedHubspotPages.blogPosts.forEach((page: any) => {
          blogPages.push({
            id: page.id,
            name: page.name,
            url: page.absolute_url,
            state: page.state,
            pageType: "BLOG",
          });
        });
      }

      if (fetchedHubspotPages.pages.length !== 0) {
        fetchedHubspotPages.pages.forEach((page: any) => {
          const pageCommonFields = {
            id: page.id,
            name: page.name,
            url: page.absolute_url,
            state: page.state,
          };
          if (page.analytics_page_type === "landing-page") {
            landingPages.push({
              ...pageCommonFields,
              pageType: "LANDING",
            });
          } else {
            webPages.push({
              ...pageCommonFields,
              pageType: "WEB",
            });
          }
        });
      }

      dispatch(setFoundHubspostPages([...landingPages, ...webPages, ...blogPages]));
      dispatch(setIsPending(false));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getHubspotPages: ", error);
      dispatch(setIsPending(false));
    }
  };

export const getCampaignFoundCompaniesInfo =
  (
    queryOpts: Array<any>,
    setInfoIsLoading: (state: boolean) => void,
  ): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      const { campaignToCreate } = getState();
      const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);

      const data = {
        bgAdvertiserId: campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId,
        filters: queryOpts,
        after: "0",
        numberResults: 100,
        reqID: "test",
      };
      socket.emit("getTargetedCompanies", data);

      socket.on("retrievedCompanies", (companyInfoToDisplay: any) => {
        try {
          setInfoIsLoading(false);
          socket.close();
          dispatch(handleApiResponse(companyInfoToDisplay, setFoundCompanyInfo, setApiError));
        } catch (error) {
          dispatch(setApiError((error as any).toString()));
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getCampaignFoundCompaniesInfo: ", error);
    }
  };

export const getFoundCompaniesInfoFromLists =
  (
    hubspotListId: number[],
    setInfoIsLoading: (state: boolean) => void,
  ): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      const { campaignToCreate } = getState();
      const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);

      const data = {
        bgAdvertiserId: campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId,
        hubspotListId,
      };
      socket.emit("getCompaniesFromList", data);

      socket.on("retrievedCompaniesFromList", (companyInfoToDisplay: any) => {
        try {
          setInfoIsLoading(false);
          socket.close();
          dispatch(handleApiResponse(companyInfoToDisplay, setFoundCompanyInfo, setApiError));
        } catch (error) {
          dispatch(setApiError((error as any).toString()));
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getCampaignFoundCompaniesInfo: ", error);
      setInfoIsLoading(false);
    }
  };

export const getFoundEmailsInfo =
  (
    hubspotListId: number[],
    setInfoIsLoading: (state: boolean) => void,
  ): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      const { campaignToCreate } = getState();
      const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);

      const data = {
        bgAdvertiserId: campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId,
        hubspotListId,
      };
      socket.emit("getContactsFromList", data);

      socket.on("retrievedContacts", (emailsInfoToDisplay: any) => {
        try {
          socket.close();
          const payload = emailsInfoToDisplay?.payload;

          if (!payload) {
            dispatch(
              handleApiResponse(
                { success: true, payload: { emailsToHash: [], fromFile: null } },
                setFoundEmailsInfo,
                setApiError,
              ),
            );
            setInfoIsLoading(false);
            return;
          }

          const { validatedData, errorMessage } = validateHubSpotData(payload, EmailRegExp, "Emails");

          const responsePayload =
            validatedData.length > 0
              ? { success: true, payload: processEmails(validatedData) }
              : { success: true, payload: { emailsToHash: [], fromFile: null } };

          dispatch(handleApiResponse(responsePayload, setFoundEmailsInfo, setApiError));

          if (errorMessage) errorToaster(errorMessage, 10000);
        } catch (error) {
          dispatch(setApiError(String(error)));
        } finally {
          setInfoIsLoading(false);
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("getCampaignFoundCompaniesInfo: ", error);
      setInfoIsLoading(false);
    }
  };

const getCountries = (selectedRegions: Array<string>) => {
  const countries: Array<string> = [];
  if (selectedRegions) {
    selectedRegions.map((region: string) => {
      if (countries.length === 2) {
        return [];
      }

      if (region.includes("USA") && !countries.includes("USA")) {
        countries.push("USA");
      }

      if (region.includes("CAN") && !countries.includes("CAN")) {
        countries.push("CAN");
      }

      // eslint-disable-next-line consistent-return
      return null;
    });

    return countries;
  }

  return [];
};

export const resetCampaignToCreateState =
  (): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> =>
  async (dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>): Promise<void> => {
    dispatch(setResetCampaignToCreate());
  };

export const createAdGroup = async (adGroupToCreate: AdGroupCreationBody) => {
  try {
    const { payload: createdAdGroup } = await api.createAdGroup(adGroupToCreate);
    return createdAdGroup;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

export const createAdGroupTemplate = async (adGroupTemplateToCreateBody: AdGroupTemplateCreationBody) => {
  try {
    const { payload: createdAdGroupTemplate } = await api.createAdGroupTemplate(adGroupTemplateToCreateBody);
    return createdAdGroupTemplate;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

export const isCreativeAssetFromFile = (asset: CreativeAssetToCreate): asset is CreativeAssetFromFile => {
  return "file" in asset;
};

export const createTemplateCreativeBody = (
  createdCreative: CreativeAsset,
  creativeAsset: CreativeAssetToCreate,
  adGroupTemplateId: string,
  duration?: string,
) => {
  const form = new FormData();
  if (isCreativeAssetFromFile(creativeAsset)) {
    form.append("size", creativeAsset.file.size.toString());
  } else {
    form.append("size", creativeAsset.size.toString());
  }
  form.append("id", `bg-creative-template-${uuid.v4()}`);
  form.append("adGroupTemplateId", adGroupTemplateId);
  form.append("creativeTemplateSrc", createdCreative.creativeSrc);
  form.append("creativeType", createdCreative.creativeType);
  form.append("name", createdCreative.name);
  form.append("description", createdCreative.description);
  form.append("height", createdCreative.height!.toString());
  form.append("width", createdCreative.width!.toString());
  form.append("beeswaxCreativeId", createdCreative.beeswaxCreativeId.toString());
  if (duration) form.append("duration", duration);
  return form;
};

export const createCreativeTemplateAsset = async (
  createdCreativeAsset: CreativeAsset,
  creativeAsset: CreativeAssetToCreate,
  adGroupTemplateId: string,
  duration?: string,
) => {
  const form = createTemplateCreativeBody(createdCreativeAsset, creativeAsset, adGroupTemplateId, duration);
  await api.createCreativeTemplate(form);
};

export const createCreativeBody = (
  creativeAsset: CreativeAssetToCreate,
  beeswaxAdvertiserId: string,
  brandgenAdvertiserId: string,
  landingPage: string,
  adGroupId: string,
  duration?: string,
) => {
  const form = new FormData();
  if (isCreativeAssetFromFile(creativeAsset)) {
    form.append("name", creativeAsset.file.name);
    form.append("file", creativeAsset.file);
    form.append("size", creativeAsset.file.size.toString());
  } else {
    form.append("sourceUrl", creativeAsset.sourceUrl);
    form.append("name", creativeAsset.name);
    form.append("size", creativeAsset.size.toString());
    form.append("mimetype", creativeAsset.mimetype);
  }

  form.append("description", "");
  form.append("height", creativeAsset.height!.toString());
  form.append("width", creativeAsset.width!.toString());
  form.append("advertiser_id", beeswaxAdvertiserId);
  form.append("bg_advertiser_id", brandgenAdvertiserId);
  form.append("bg_creative_id", `bgCreative-${uuid.v4()}`);
  form.append("click_url", landingPage);
  form.append("adGroupId", adGroupId);

  if (duration) form.append("duration", creativeAsset.duration?.toString() ?? "60");
  return form;
};

/* eslint-disable no-await-in-loop */
export const uploadCreativeAssets = async (
  creativeAssets: CreativeAssetToCreate[],
  beeswaxAdvertiserId: string,
  brandgenAdvertiserId: string,
  landingPage: string,
  adGroupId: string,
  apiFunction: (form: FormData) => Promise<APIResponse>,
  brandgenCampaignId?: string,
) => {
  const uploadedCreatives = [];

  for (const creativeAsset of creativeAssets) {
    const form = createCreativeBody(
      creativeAsset,
      beeswaxAdvertiserId,
      brandgenAdvertiserId,
      landingPage,
      adGroupId,
      creativeAsset.duration?.toString(),
    );

    if (brandgenCampaignId) {
      form.append("brandgenCampaignId", brandgenCampaignId);
    }

    const { payload: createdCreative } = await apiFunction(form);
    if (createdCreative) {
      uploadedCreatives.push({ ...createdCreative, originalAsset: creativeAsset });
    }
  }

  return uploadedCreatives;
};

export const createCreativeTemplates = async (
  creatives: (CreativeAsset & { originalAsset: CreativeAssetToCreate })[],
  adGroupTemplateId: string,
): Promise<void> => {
  for (const creative of creatives) {
    const { originalAsset } = creative;

    if (isCreativeAssetFromFile(originalAsset)) {
      await createCreativeTemplateAsset(creative, originalAsset, adGroupTemplateId, originalAsset.duration?.toString());
    }
  }
};

const getCreativesByAdGroupId = async (adGroupId: string) => {
  try {
    const { payload: adGroupCreatives } = await api.getCreativesByAdGroup(adGroupId);
    return adGroupCreatives;
  } catch (error) {
    const err = error as any;
    throw new Error(err);
  }
};

export const createCampaign = (
  onCampaignCreated: (createdCampaign: CampaignType) => void,
): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (
    dispatch: ThunkDispatch<ReduxApplicationState, {}, AnyAction>,
    getState: () => ReduxApplicationState,
  ): Promise<void> => {
    try {
      dispatch(setIsPending(true));

      const state = getState();
      const { user, campaignToCreate } = state;
      const { basicDetails, selectedAdvertiser } = campaignToCreate;
      const { agencyId } = basicDetails;
      const socket = socketIOClient(process.env.REACT_APP_BACKEND_URL!);

      // Campaign id generation
      const brandgenCampaignId = `bg-campaign-${uuid.v4()}`;

      // Create Ad Group
      const adGroupToCreateBody = {
        adGroupId: `bg-ad-group-${uuid.v4()}`,
        brandgenAdvertiserId: campaignToCreate.selectedAdvertiser?.brandgenAdvertiserId!,
        brandgenCampaignId,
        createdBy: user.currentUser,
        name: campaignToCreate.adGroupToCreate.name,
        description: campaignToCreate.adGroupToCreate.description ?? "",
        adGroupType: campaignToCreate.adGroupToCreate.adGroupType,
      };
      const createdAdGroup: AdGroup = await createAdGroup(adGroupToCreateBody);

      // Create Ad Group template
      let createdAdGroupTemplate: AdGroupTemplate = {
        id: "",
        brandgenAdvertiserId: "",
        createdBy: "",
        name: "",
        description: "",
        adGroupType: AdGroupType.IMAGE,
        createdAt: "",
        updatedAt: "",
      };
      if (!campaignToCreate.useAdGroupTemplate && createdAdGroup) {
        const adGroupTemplateToCreateBody: AdGroupTemplateCreationBody = {
          id: `bg-ad-group-template-${uuid.v4()}`,
          brandgenAdvertiserId: createdAdGroup.brandgenAdvertiserId!,
          createdBy: createdAdGroup.createdBy,
          name: createdAdGroup.name,
          description: createdAdGroup.description,
          adGroupType: createdAdGroup.adGroupType,
        };
        createdAdGroupTemplate = await createAdGroupTemplate(adGroupTemplateToCreateBody);
      }

      // Create Creatives and tempaltes
      try {
        const uploadedCreatives = await uploadCreativeAssets(
          campaignToCreate.adGroupToCreate.creativeAssets,
          selectedAdvertiser!.beeswaxAdvertiserId,
          selectedAdvertiser!.brandgenAdvertiserId,
          campaignToCreate.landingPage,
          createdAdGroup.adGroupId,
          api.createCreative,
          brandgenCampaignId,
        );

        if (!campaignToCreate.useAdGroupTemplate && createdAdGroupTemplate.id) {
          await createCreativeTemplates(uploadedCreatives, createdAdGroupTemplate.id);
        }
      } catch (error) {
        console.error("Error uploading creatives or creating templates", error);
        throw error;
      }
      const createdCreatives: CreativeAssetsByAdGroup = await getCreativesByAdGroupId(createdAdGroup.adGroupId);

      // Create campaign
      const body = {
        brandgenCampaignId,
        agencyId,
        beeswaxCampaign: {
          active: false,
          advertiser_id: Number.parseInt(selectedAdvertiser!.beeswaxAdvertiserId),
          budget_type: 2,
          campaign_budget: basicDetails.estimatedBudget,
          dailyBudget: basicDetails.dailyBudget,
          campaign_name: basicDetails.name,
          start_date:
            typeof basicDetails.startDate === "string"
              ? new Date(Date.parse(basicDetails.startDate))
              : basicDetails.startDate,
          end_date:
            typeof basicDetails.endDate === "string"
              ? new Date(Date.parse(basicDetails.endDate))
              : basicDetails.endDate,
        },
        max_bid: basicDetails.maxBid,
        brandgenAdvertiserId: selectedAdvertiser!.brandgenAdvertiserId,
        createdBy: basicDetails.createdBy,
        creativeIds: createdCreatives.creatives.map(
          (creative: CreativeAsset) => creative.beeswaxCreativeId,
        ) as number[],
        description: basicDetails.description,
        name: basicDetails.name,
        targetingInfo: {
          domain_list: campaignToCreate.selectedDomainListsIds,
          geo_country: getCountries(campaignToCreate.selectedGeoTargetOptions),
          geo_state: campaignToCreate.selectedGeoTargetOptions,
          geo_zip: campaignToCreate.selectedZipCodes,
          role_title_segments: getCookieStrings(campaignToCreate.campaignCookieSets as Record<string, CookieSet[]>),
          retargeting_segments: campaignToCreate.selectedSegmentIds,
        },
        foundCompanyInfo: {
          companies: [
            ...(campaignToCreate.foundCompanyInfo?.primarySegments?.filter(
              (company) => company.include && company.domainInfo,
            ) || []),
            ...(campaignToCreate.foundCompanyInfo?.uniqueSegments?.filter(
              (company) => company.include && company.domainInfo,
            ) || []),
          ],
          fromFile: campaignToCreate.foundCompanyInfo?.fromFile,
        },
        publicDomainLists: campaignToCreate.selectedDomainLists,
        foundEmailsInfo: campaignToCreate.foundEmailsInfo,
        matches: campaignToCreate.foundCompanyInfo?.matches,
        isVideoCampaign: basicDetails.isVideoCampaign,
        showInHubspot: basicDetails.showInHubspot,
        maxCompanies: basicDetails.maxCompanies,
        campaignType: localStorage.getItem("customCampaignType"),
      };

      localStorage.removeItem("customCampaignType");

      socket.emit("createCampaign", body);

      socket.on("campaignCreated", (response: APIResponse) => {
        socket.close();
        dispatch(setIsPending(false));
        onCampaignCreated(response.payload);
      });

      socket.on("campaignCreationError", (error: any) => {
        // eslint-disable-next-line no-console
        console.log("createCampaign: ", error);
        dispatch(setIsPending(false));
        socket.close();
      });
      dispatch(setIsPending(true));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("createCampaign: ", error);
      dispatch(setIsPending(false));
    }
  };
};

// Campaign Templates
const createOrGetAdGroupTemplate = async (campaignToCreate: CampaignCreatorStore, userId: string): Promise<string> => {
  if (campaignToCreate.useAdGroupTemplate) {
    return campaignToCreate.selectedAdGroupTemplate!;
  }

  const adGroupTemplateToCreateBody: AdGroupTemplateCreationBody = {
    id: `bg-ad-group-template-${uuid.v4()}`,
    brandgenAdvertiserId: campaignToCreate.selectedAdvertiser?.brandgenAdvertiserId!,
    createdBy: userId,
    name: campaignToCreate.adGroupToCreate.name,
    description: campaignToCreate.adGroupToCreate.description ?? "",
    adGroupType: campaignToCreate.adGroupToCreate.adGroupType,
  };

  const createdAdGroup = await createAdGroupTemplate(adGroupTemplateToCreateBody);

  const uploadedCreatives = await uploadCreativeAssets(
    campaignToCreate.adGroupToCreate.creativeAssets,
    campaignToCreate.selectedAdvertiser!.beeswaxAdvertiserId,
    campaignToCreate.selectedAdvertiser!.brandgenAdvertiserId,
    campaignToCreate.landingPage,
    createdAdGroup.adGroupId,
    api.uploadCreativeOnBeeswax,
  );

  await createCreativeTemplates(uploadedCreatives, createdAdGroup.id);
  return createdAdGroup.id;
};

const buildCampaignTemplateBody = (
  campaignToCreate: CampaignCreatorStore,
  userId: string,
  selectedAdGroupTemplate: string,
) => {
  const { basicDetails, selectedAdvertiser } = campaignToCreate;

  return {
    agencyId: basicDetails.agencyId,
    selectedAdvertiser: selectedAdvertiser?.brandgenAdvertiserId,
    templateName: campaignToCreate.campaignTemplateName,
    templateSteps: campaignToCreate.campaignSteps,
    advertiser_id: Number.parseInt(selectedAdvertiser?.beeswaxAdvertiserId ?? "0"),
    estimatedBudget: basicDetails.estimatedBudget,
    dailyBudget: basicDetails.dailyBudget,
    campaign_name: basicDetails.name,
    landingPage: campaignToCreate.landingPage,
    startDate: basicDetails.startDate,
    endDate: basicDetails.endDate,
    start_date: new Date(basicDetails.startDate),
    end_date: new Date(basicDetails.endDate),
    maxBid: basicDetails.maxBid,
    brandgenAdvertiserId: selectedAdvertiser?.brandgenAdvertiserId,
    createdBy: basicDetails.createdBy ?? userId,
    selectedAdGroupTemplate,
    description: basicDetails.description,
    name: basicDetails.name,
    roleTitles: Object.keys(campaignToCreate.campaignCookieSets || {})
      .filter((key) => !key.includes("_ALL"))
      .map((key) => {
        const [roleTypeId, roleId] = key.split(":");
        return { roleTypeId, roleId };
      }),
    publicDomainLists: campaignToCreate.selectedDomainLists,
    foundCompanyInfo: campaignToCreate.foundCompanyInfo,
    foundEmailsInfo: campaignToCreate.foundEmailsInfo,
    matches: campaignToCreate.foundCompanyInfo?.matches,
    isVideoCampaign: basicDetails.isVideoCampaign,
    showInHubspot: basicDetails.showInHubspot,
    maxCompanies: basicDetails.maxCompanies,
    isCustomCampaign: !!window.location.href.includes("create/custom"),
    selectedDomainListsIds: campaignToCreate.selectedDomainListsIds,
    selectedGeoTargetOptions: campaignToCreate.selectedGeoTargetOptions,
    selectedZipCodes: campaignToCreate.selectedZipCodes,
    selectedSegmentIds: campaignToCreate.selectedSegmentIds,
  };
};

export const createCampaignTemplate = (
  onCampaignCreated: (createdCampaign: CampaignType) => void,
): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch, getState) => {
    try {
      dispatch(setIsPending(true));
      const { user, campaignToCreate } = getState();

      const selectedAdGroupTemplate = await createOrGetAdGroupTemplate(campaignToCreate, user.currentUser);

      const body = buildCampaignTemplateBody(campaignToCreate, user.currentUser, selectedAdGroupTemplate);

      const createdCampaignTemplate = await api.createCampaignTemplate(body);
      onCampaignCreated(createdCampaignTemplate);
    } catch (error) {
      console.error("createCampaignTemplate:", error);
    } finally {
      dispatch(setIsPending(false));
    }
  };
};

export const updateCampaignTemplate = (
  onCampaignCreated: (createdCampaign: CampaignType) => void,
): ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction> => {
  return async (dispatch, getState) => {
    try {
      dispatch(setIsPending(true));
      const { user, campaignToCreate } = getState();

      if (!campaignToCreate?.campaignTemplate?.id) {
        console.error("updateCampaignTemplate: Missing campaign template Id");
        return;
      }

      const selectedAdGroupTemplate = await createOrGetAdGroupTemplate(campaignToCreate, user.currentUser);

      const removedSteps = campaignToCreate.campaignTemplate.templateSteps.filter(
        (step) => !campaignToCreate?.campaignSteps?.includes(step),
      );

      for (const step of removedSteps) {
        switch (step) {
          case CAMPAIGN_STEPS.ROLE_TITLES:
            dispatch(setCampaignCookies(null));
            break;
          case CAMPAIGN_STEPS.CRM_RETARGETING_SEGMENTS:
            dispatch(setFoundEmailsInfo(null));
            break;
          case CAMPAIGN_STEPS.PUBLICATION_LISTS:
            dispatch(setSelectedDomainLists([]));
            break;
          case CAMPAIGN_STEPS.STATE_PROVINCES:
            dispatch(setSelectedGeoTargetOptions([]));
            break;
          case CAMPAIGN_STEPS.ZIP_CODES:
            dispatch(setSelectedZipCodes([]));
            break;
          case CAMPAIGN_STEPS.TARGET_COMPANIES:
            dispatch(setFoundCompanyInfo(null));
            break;
          case CAMPAIGN_STEPS.VISITORS_RETARGETING_SEGMENTS:
            dispatch(setSelectedSegmentIds([]));
            break;
          default:
            break;
        }
      }
      const { campaignToCreate: updatedCampaignToCreate } = getState();

      const body = buildCampaignTemplateBody(updatedCampaignToCreate, user.currentUser, selectedAdGroupTemplate);

      const updatedCampaignTemplate = await api.updateCampaignTemplate(campaignToCreate.campaignTemplate.id, body);

      onCampaignCreated(updatedCampaignTemplate);
    } catch (error) {
      console.error("updateCampaignTemplate:", error);
    } finally {
      dispatch(setIsPending(false));
    }
  };
};
